import { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useAppStore } from '../../stores/appStore';
import { useUIStore } from '../../stores/uiStore';
import { ThemeToggleCompact } from '../../components/common/theme-toggle';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { LoadingSpinner } from '../../components/ui/loading-spinner';
import { Alert, AlertDescription, AlertTitle } from '../../components/ui/alert';
import { Badge } from '../../components/ui/badge';
import { Plus, User, Copy, AlertCircle, Lock, Unlock, Mail, Edit2, CheckCircle, XCircle } from 'lucide-react';
import { formatDate } from '../../lib/utils';
import { toast } from '../../components/ui/toast';
import { api, lockUser, unlockUser, activateUser } from '../../services/api';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import type { User as UserType } from '../../types';

export function UserList() {
  const { isSuperAdmin } = useAuth();
  const { organizations, fetchOrganizations } = useAppStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [users, setUsers] = useState<UserType[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);

  // Get orgId from navigation state (when returning from create page)
  const stateOrgId = (location.state as any)?.orgId;

  // Initialize dark mode
  useEffect(() => {
    const savedDarkMode = useUIStore.getState().darkMode;
    if (savedDarkMode) {
      document.documentElement.classList.add('dark');
    }
  }, []);

  // Load organizations for super admin
  useEffect(() => {
    if (isSuperAdmin()) {
      fetchOrganizations().catch(() => {});
    }
  }, [isSuperAdmin, fetchOrganizations]);

  // Set orgId from navigation state or localStorage on mount
  useEffect(() => {
    if (!isSuperAdmin()) return;

    // Priority: 1. Navigation state (from create), 2. localStorage, 3. null
    if (stateOrgId) {
      setOrgId(stateOrgId);
      // Clear the navigation state to avoid persisting it
      window.history.replaceState({}, '');
    } else {
      const savedOrgId = localStorage.getItem('lastSelectedOrgId');
      if (savedOrgId) {
        setOrgId(savedOrgId);
      }
    }
  }, [isSuperAdmin, stateOrgId]);

  // Fetch users when org is selected
  useEffect(() => {
    if (!orgId || !isSuperAdmin()) return;

    const fetchUsers = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await api.get(`api/v1/organizations/${orgId}/users`).json() as { users: UserType[] };
        setUsers(response.users || []);
      } catch (err: any) {
        setError(err.message || 'Failed to fetch users');
        console.error('Fetch users error:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchUsers();
  }, [orgId, isSuperAdmin]);

  const handleOrgSelect = (value: string) => {
    setOrgId(value);
    // Save to localStorage for persistence
    localStorage.setItem('lastSelectedOrgId', value);
  };

  const handleCreateClick = () => {
    if (orgId) {
      navigate('/dashboard/users/create', { state: { orgId } });
    }
  };

  const handleEditClick = (userId: string) => {
    if (orgId) {
      navigate(`/dashboard/users/${userId}`, { state: { orgId } });
    } else {
      navigate(`/dashboard/users/${userId}`);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copied!',
      description: `${label} copied to clipboard`,
    });
  };

  const handleLockUser = async (userId: string, userName: string) => {
    try {
      const response = await lockUser(userId);
      toast({
        title: 'User Locked',
        description: `${userName} has been locked${response.duration_minutes ? ` for ${response.duration_minutes} minutes` : ''}`,
      });
      // Refresh the user list
      if (orgId) {
        const usersResponse = await api.get(`api/v1/organizations/${orgId}/users`).json() as { users: UserType[] };
        setUsers(usersResponse.users || []);
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to lock user',
        variant: 'destructive',
      });
    }
  };

  const handleUnlockUser = async (userId: string, userName: string) => {
    try {
      await unlockUser(userId);
      toast({
        title: 'User Unlocked',
        description: `${userName} has been unlocked`,
      });
      // Refresh the user list
      if (orgId) {
        const usersResponse = await api.get(`api/v1/organizations/${orgId}/users`).json() as { users: UserType[] };
        setUsers(usersResponse.users || []);
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to unlock user',
        variant: 'destructive',
      });
    }
  };

  const handleResendVerification = async (email: string) => {
    try {
      await api.post('api/v1/users/send-verification', { json: { email } }).json();
      toast({
        title: 'Verification Email Sent',
        description: `Verification email sent to ${email}`,
      });
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to send verification email',
        variant: 'destructive',
      });
    }
  };

  const handleActivateUser = async (userId: string, userName: string, emailVerified: boolean, isActive: boolean) => {
    try {
      await activateUser(userId, { email_verified: emailVerified, is_active: isActive });
      toast({
        title: 'User Activated',
        description: `${userName} has been ${isActive ? 'activated' : 'deactivated'}${emailVerified ? ' and email verified' : ''}`,
      });
      // Refresh the user list
      if (orgId) {
        const usersResponse = await api.get(`api/v1/organizations/${orgId}/users`).json() as { users: UserType[] };
        setUsers(usersResponse.users || []);
      }
    } catch (err: any) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to activate user',
        variant: 'destructive',
      });
    }
  };

  if (!isSuperAdmin()) {
    return (
      <div className="space-y-4">
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Access Denied</AlertTitle>
          <AlertDescription>
            Only super administrators can manage users.
          </AlertDescription>
        </Alert>
        <Link to="/dashboard">
          <Button variant="outline">Back to Dashboard</Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="Logo" className="h-10 w-auto object-contain" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">User Management</h1>
            <p className="text-gray-600 dark:text-gray-400 text-sm mt-0.5">Manage users for selected organization</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button onClick={handleCreateClick} disabled={!orgId}>
            <Plus className="h-4 w-4 mr-2" />
            Create User
          </Button>
          <ThemeToggleCompact />
        </div>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Organization Selector for Super Admin */}
      <Card>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center gap-2">
            <p className="text-sm text-gray-600 dark:text-gray-400">
              <strong>Note:</strong> Select an organization to view and manage its users.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium">Organization:</span>
            <Select value={orgId || ''} onValueChange={handleOrgSelect}>
              <SelectTrigger className="w-[300px]">
                <SelectValue placeholder="Select an organization...">
                  {orgId && organizations.find(o => o.id === orgId)?.name}
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {organizations.map((org) => (
                  <SelectItem key={org.id} value={org.id}>
                    {org.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {!orgId ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <User className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              Select an Organization
            </h3>
            <p className="text-gray-600 dark:text-gray-400">
              Please select an organization from above to view its users
            </p>
          </CardContent>
        </Card>
      ) : loading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size={48} />
        </div>
      ) : users.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <User className="h-12 w-12 text-gray-400 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No Users Yet
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-4">
              Create your first user for this organization
            </p>
            <Button onClick={handleCreateClick}>
              Create User
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {users.map((user) => (
            <Card key={user.id}>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-blue-600" />
                    <CardTitle className="text-lg">{user.name || user.email}</CardTitle>
                    <Badge variant={user.role === 'super_admin' ? 'destructive' : user.role === 'admin' ? 'default' : 'secondary'}>
                      {user.role === 'super_admin' ? 'Super Admin' : user.role === 'admin' ? 'Org Admin' : 'User'}
                    </Badge>
                    {user.locked && (
                      <Badge variant="destructive" className="flex items-center gap-1">
                        <Lock className="h-3 w-3" />
                        Locked
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditClick(user.id)}
                    >
                      <Edit2 className="h-4 w-4 mr-2" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleResendVerification(user.email)}
                    >
                      <Mail className="h-4 w-4 mr-2" />
                      Resend Email
                    </Button>
                    {user.is_active ? (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-orange-600 hover:text-orange-700"
                        onClick={() => handleActivateUser(user.id, user.name || user.email, user.email_verified, false)}
                      >
                        <XCircle className="h-4 w-4 mr-2" />
                        Deactivate
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-green-600 hover:text-green-700"
                        onClick={() => handleActivateUser(user.id, user.name || user.email, true, true)}
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Activate
                      </Button>
                    )}
                    {user.locked ? (
                      <Button
                        variant="default"
                        size="sm"
                        className="bg-green-600 hover:bg-green-700"
                        onClick={() => handleUnlockUser(user.id, user.name || user.email)}
                      >
                        <Unlock className="h-4 w-4 mr-2" />
                        Unlock
                      </Button>
                    ) : (
                      <Button
                        variant="destructive"
                        size="sm"
                        className="bg-red-600 hover:bg-red-700"
                        onClick={() => handleLockUser(user.id, user.name || user.email)}
                      >
                        <Lock className="h-4 w-4 mr-2" />
                        Lock
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">User ID:</span>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="bg-gray-100 dark:bg-gray-800 dark:text-gray-200 px-2 py-1 rounded text-xs font-mono">
                        {user.id}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(user.id, 'User ID')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  <div>
                    <span className="text-gray-500">Email:</span>
                    <div className="flex items-center gap-2 mt-1">
                      <code className="bg-gray-100 dark:bg-gray-800 dark:text-gray-200 px-2 py-1 rounded text-xs font-mono">
                        {user.email}
                      </code>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(user.email, 'Email')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                  {user.org_id && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Organization ID:</span>
                      <div className="flex items-center gap-2 mt-1">
                        <code className="bg-gray-100 dark:bg-gray-800 dark:text-gray-200 px-2 py-1 rounded text-xs font-mono">
                          {user.org_id}
                        </code>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyToClipboard(user.org_id!, 'Organization ID')}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  )}
                  <div>
                    <span className="text-gray-500">Created:</span>
                    <span className="ml-2">{formatDate(user.created_at)}</span>
                  </div>
                    <div>
                      <span className="text-gray-500">Email Verified:</span>
                      <span className="ml-2">
                        {user.email_verified ? (
                          <Badge variant="default" className="bg-green-500">Yes</Badge>
                        ) : (
                          <Badge variant="secondary">No</Badge>
                        )}
                      </span>
                    </div>
                    <div>
                      <span className="text-gray-500">Status:</span>
                      <span className="ml-2">
                        {user.is_active ? (
                          <Badge variant="default" className="bg-green-500">Active</Badge>
                        ) : (
                          <Badge variant="secondary">Inactive</Badge>
                        )}
                      </span>
                    </div>
                  {user.locked && user.locked_until && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Locked Until:</span>
                      <span className="ml-2">{formatDate(user.locked_until)}</span>
                    </div>
                  )}
                  {user.failed_attempts !== undefined && user.failed_attempts > 0 && (
                    <div>
                      <span className="text-gray-500 dark:text-gray-400">Failed Attempts:</span>
                      <span className="ml-2">{user.failed_attempts}</span>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
